import type { BaseServiceOptions, Pagination } from '@/core/types'
import {
  serviceConfig,
  commentsServiceHandlers,
  type TComment,
  type TComments,
} from '../../..'
import { useNotification } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'

const { model } = serviceConfig.comments

export const useCommentsQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage = 1, enableQuery, filters } = options

  const { errorHttpNotification } = useNotification()

  const addRecord = useMutation<TComment, AxiosError, TComment>(
    (payload: TComment) => commentsServiceHandlers.addRecord(payload),
    {
      onError: (error) => errorHttpNotification(error),
    },
  )

  const loadEstimationComments = useQuery<TComments, AxiosError>(
    [model, currentPage, filters?.loadEstimationComments, 'estimation'],
    () =>
      commentsServiceHandlers.loadEstimationComments({
        dynamicFilters: filters?.loadEstimationComments,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadEstimationComments,
      onError: (error) => errorHttpNotification(error),
      select: (payload) =>
        payload.map((comment) => {
          const names = comment.user?.split(' ')
          const authorInitials =
            (names?.length ?? 0) >= 2
              ? `${names?.[0].charAt(0)}${names?.[1].charAt(0)}`
              : `${names?.[0].charAt(0)}`

          return {
            ...comment,
            author_initials: authorInitials,
          }
        }),
    },
  )

  const loadSellerComments = useQuery<TComments, AxiosError>(
    [model, currentPage, filters?.loadSellerComments, 'seller'],
    () =>
      commentsServiceHandlers.loadSellerComments({
        dynamicFilters: filters?.loadSellerComments,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadSellerComments,
      onError: (error) => errorHttpNotification(error),
      select: (payload) =>
        payload.map((comment) => {
          const names = comment.user?.split(' ')
          const authorInitials =
            (names?.length ?? 0) >= 2
              ? `${names?.[0].charAt(0)}${names?.[1].charAt(0)}`
              : `${names?.[0].charAt(0)}`

          return {
            ...comment,
            author_initials: authorInitials,
          }
        }),
    },
  )

  return {
    loadEstimationComments,
    loadSellerComments,
    addRecord,
  }
}
